import type { Policy } from '@/types/policy'
import type { PoliciesResponse } from '@/types/responses/policiesResponse'
import type { PolicyDocumentsResponse } from '@/types/responses/policyDocumentsResponse'
import type { DownloadPolicyDocumentResponse } from '@/types/responses/downloadPolicyDocumentResponse'
import type { GetExplanationOfBenefitsDocumentResponse } from '@/types/responses/getExplanationOfBenefitsDocumentResponse'
import type { GetUploadUrlForDocumentRequest } from '@/types/requests/getUploadUrlForDocumentRequest'
import type { GetUploadUrlForDocumentResponse } from '@/types/responses/getUploadUrlForDocumentResponse'
import type { CheckPolicyStatusesResponse, PolicyStatuses } from '@/types/responses/checkPolicyStatusesResponse'
import { parseLocalDate } from '@/utils/dateUtils'

interface State {
  policies: Policy[]
  selectedPolicy: Policy
  policyStatuses: PolicyStatuses | null
}

export const usePolicyStore = defineStore('policy', {
  state: (): State => ({
    policies: [],
    selectedPolicy: {} as Policy,
    policyStatuses: null
  }),
  getters: {
    hasPendingChanges: (state) =>
      Boolean(state.policyStatuses?.pendingEndorsementPresent || state.policyStatuses?.multipleRenewalEndorsements),
    isBillDayTooClose: (state) => Boolean(state.policyStatuses?.billDayTooClose),
    isRenewalTooClose: (state) => Boolean(state.policyStatuses?.tooCloseToRenewal),
    // policyStatuses is null both before the first response and after a failed request. The other getters treat
    // null as "not restricted", so use this to tell "no restrictions" apart from "not known yet".
    policyStatusesLoaded: (state) => state.policyStatuses !== null,
    endorsementsRestricted: (state) => (state.policyStatuses ? !state.policyStatuses.isValid : false),
    hasPolicy: (state) => Boolean(state.selectedPolicy?.policyId),
    activePolicies: (state) => state.policies.filter((policy) => policy.isActive) || [],
    cancelledPolicies: (state) => state.policies.filter((policy) => !policy.isActive) || [],
    scheduledRenewalDate: (state) => {
      return state.selectedPolicy.scheduledRenewalDate
        ? parseLocalDate(state.selectedPolicy.scheduledRenewalDate).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          })
        : ''
    },
    policyEndDate: (state) => {
      return state.selectedPolicy.policyEndDate
        ? parseLocalDate(state.selectedPolicy.policyEndDate).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          })
        : ''
    },
    earliestClaimDate: (state) => {
      return state.selectedPolicy.earliestClaimDate
        ? parseLocalDate(state.selectedPolicy.earliestClaimDate).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          })
        : ''
    },
    policyUnderCharged: (state) => {
      return state.selectedPolicy?.epiDummy?.includes('UC')
    }
  },
  actions: {
    setPolicies(policies: Policy[]) {
      this.policies = policies
    },
    setSelectedPolicy(policy: Policy | undefined) {
      const isSamePolicy = Boolean(policy?.policyId) && policy?.policyId === this.selectedPolicy?.policyId

      this.selectedPolicy = policy || ({} as Policy)

      // The token refresh re-selects the current policy each time. The statuses are still valid for it, so keep
      // them rather than clearing them and making another backend call (which includes a Stripe lookup).
      if (isSamePolicy && this.policyStatuses) {
        return
      }

      this.policyStatuses = null

      if (this.selectedPolicy.policyId) {
        this.refreshPolicyStatuses()
      }
    },
    setNewPolicyId(policyId: string) {
      if (this.selectedPolicy) {
        this.selectedPolicy.policyId = policyId
        this.refreshPolicyStatuses()
      }
    },
    async getPolicies() {
      const { response } = useApi(`policy/policies`)
      const { policies } = (await response) as PoliciesResponse

      // Filter and prevent duplication using policyId for uniqueness
      const uniquePolicies = policies.filter(
        (policy) => !this.policies.some((existingPolicy) => existingPolicy.policyId === policy.policyId)
      )
      // Update state with concatenated list of policies
      this.setPolicies(this.policies.concat(uniquePolicies))

      return policies
    },
    setPolicyStatuses(statuses: PolicyStatuses | null) {
      this.policyStatuses = statuses
    },
    async checkPolicyStatuses(policyId?: string) {
      const id = policyId || this.selectedPolicy?.policyId

      if (!id) {
        return null
      }

      const { response } = useApi(`contact/checkpolicystatusesbypolicyid`, {
        method: 'POST',
        body: { policyId: id },
        suppressGlobalErrorModal: true,
        abortOnNavigation: false
      })
      const { policyStatuses } = (await response) as CheckPolicyStatusesResponse

      // Ignore the result if the user switched policies while the request was in flight
      if (id === this.selectedPolicy?.policyId) {
        this.setPolicyStatuses(policyStatuses)
      }

      return policyStatuses
    },
    refreshPolicyStatuses() {
      return this.checkPolicyStatuses().catch((error) => {
        console.error('Failed to check policy statuses', error)
        return null
      })
    },
    async getPolicyDocuments() {
      const { response } = useApi(`policydocument/getdocumentlist`, {
        query: {
          policyId: this.selectedPolicy.policyId
        }
      })

      const { policyDocuments } = (await response) as PolicyDocumentsResponse

      return policyDocuments
    },
    async downloadPolicyDocument(documentFilePath: string) {
      const policyId = this.selectedPolicy.policyId
      const { response } = useApi(`policydocument/downloaddocument`, {
        query: {
          documentFilePath,
          policyId
        }
      })
      const result = (await response) as DownloadPolicyDocumentResponse
      // Convert base64 to Blob
      const byteCharacters = atob(result.fileBytes)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray])
      return { blob, fileName: result.fileName }
    },
    async downloadExplanationOfBenefitsDocument(claimId: string) {
      const policyId = this.selectedPolicy.policyId
      const { response } = useApi(`policydocument/GetExplanationOfBenefitsDocument`, {
        query: {
          policyId,
          claimId
        }
      })
      const result = (await response) as GetExplanationOfBenefitsDocumentResponse
      // Decode fileBytes as JSON and extract Data property
      const parsed = JSON.parse(atob(result.fileBytes))
      const pdfBase64 = parsed.Data
      // Convert base64 to Blob
      const byteCharacters = atob(pdfBase64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      return { blob, fileName: 'EOB.pdf' }
    },
    async getUploadUrlForDocument(document: { name: string; fileSize: number }) {
      const policyId = this.selectedPolicy.policyId
      const requestBody: GetUploadUrlForDocumentRequest = {
        policyId,
        document: {
          name: document.name,
          fileSize: document.fileSize,
          createdOn: new Date()
        }
      }

      const { response } = useApi(`document/GetUploadUrlForDocument`, {
        method: 'POST',
        body: requestBody
      })

      const result = (await response) as GetUploadUrlForDocumentResponse
      return result
    }
  },
  persist: {
    storage: piniaPluginPersistedstate.cookies({
      expires: useCookieExpire.weeks(1)
    }),
    // Bill day and renewal proximity change daily, so statuses saved in a cookie would go stale. They are
    // refetched after hydration instead.
    omit: ['policyStatuses'],
    afterHydrate: ({ store }) => {
      if (store.selectedPolicy?.policyId) {
        store.refreshPolicyStatuses()
      }
    }
  }
})
